import { Typography } from '@/shared';
import { useBreakpoints } from '@/shared/providers/Device';
import { useMicrocopy } from '@/shared/providers/Microcopy/MicrocopyHook';
import { ReadingTime } from '@/shared/types/readingTime';
import { AssetStoryblok, MindStoryblok } from '@/shared/types/storyblok/storyblok-types';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { RefObject, useEffect, useState } from 'react';
import { StoryblokStory } from 'storyblok-generate-ts';
import Container from '../Container/Container';
import { cardSubtitle } from '../FeaturedArticle/helper/subtitle';
import { SBImage } from '../ImageService/SBImage';
import RichText from '../RichText/RichText';
import styles from './ArticleHero.module.scss';

import { RichtextStoryblok } from '@/shared/types/storyblok/storyblok-types';
import { motion } from 'framer-motion';

const TranslationError = dynamic(() => import('../ArticleLayout/_components/TranslationError/TranslationError'), {
    loading: () => null,
});

type ArticleHeroProps = {
    title: string;
    intro: RichtextStoryblok | undefined;
    published: string;
    mainImage?: AssetStoryblok;
    author?: StoryblokStory<MindStoryblok>;
    client?: string;
    component?: string;
    fromModal?: boolean;
    bodyRef?: RefObject<HTMLDivElement>;
};

const ArticleHero = (props: ArticleHeroProps) => {
    const role = props.author?.content?.role ?? '';
    const subtitle = props.client && props.component ? cardSubtitle(props.client, props.component) : '';
    const { microcopy } = useMicrocopy();
    const [readingTime, setReadingTime] = useState('4 ' + microcopy?.readingTimeText);
    const [height, setHeight] = useState(0);
    const { isXXSmall, isExtraSmall, isSmall, isMedium } = useBreakpoints();
    const dateObj = new Date(props.published ?? '2023-12-06T12:42:42.774Z');
    const router = useRouter(),
        dateCountry = router.locale === 'en' ? 'en-GB' : 'de-CH';
    const formattedDate = new Intl.DateTimeFormat(dateCountry, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(dateObj);
    const isProject = props.client && subtitle;

    useEffect(() => {
        if (props.bodyRef && props.bodyRef.current && props.bodyRef.current.scrollHeight) {
            const bodyRefElem = props.bodyRef.current;
            const blokLight = bodyRefElem.querySelectorAll('div.ArticleText_light__p6_16').length;
            const blokDark = bodyRefElem.querySelectorAll('div.ArticleSection_dark___jm_6').length;
            const h2 = bodyRefElem.querySelectorAll('h2').length;
            const h3 = bodyRefElem.querySelectorAll('h3').length;
            const h4 = bodyRefElem.querySelectorAll('h4').length;
            const p = bodyRefElem.querySelectorAll('p').length;
            const img = bodyRefElem.querySelectorAll('img');
            let imgHeight = 0;
            img.forEach((image: HTMLImageElement) => {
                if (image.complete) {
                    imgHeight += image.clientHeight;
                } else {
                    image.addEventListener('load', () => {
                        imgHeight += image.clientHeight;
                    });
                }
            });
            const total =
                h2 * ReadingTime.h2 +
                h3 * ReadingTime.h3 +
                h4 * ReadingTime.h4 +
                p * ReadingTime.pPadding +
                blokLight * ReadingTime.articleBlok +
                blokDark * ReadingTime.articleBlok +
                img.length * ReadingTime.img +
                imgHeight +
                ReadingTime.bottomPadding;
            setHeight(total);
        }
    }, [props.bodyRef]);

    useEffect(() => {
        const scrollHeight = props.bodyRef?.current?.scrollHeight;
        const bodyHeight = isXXSmall
            ? (height / 24) * 9
            : isExtraSmall
            ? (height / 30) * 8
            : isSmall
            ? (height / 30) * 9
            : isMedium
            ? (height / 30) * 10
            : (height / 30) * 11;

        const wordsPerBody = scrollHeight! - bodyHeight;
        const time = Math.round(wordsPerBody / 1200);
        setReadingTime(time + ' ' + microcopy.readingTimeText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [microcopy.readingTimeText, height, props.bodyRef?.current?.scrollHeight]);

    return (
        <Container code="fullpage" className={{ wrap: styles.container }}>
            <TranslationError />
            <motion.div
                initial={{ opacity: 0, y: 670, scale: 0.9 }}
                animate={{
                    y: 0,
                    scale: 1,
                    opacity: 1,
                    transition: { duration: 0.46, stiffness: 0,  delay: 0.46, ease: 'easeInOut' },
                }}
            >
                <Container code="standard">
                    <div className={clsx(props.fromModal && styles.topPadding)}>
                        {isProject && (
                            <div className={styles.subtitle}>
                                <Typography.Small>{subtitle}</Typography.Small>
                            </div>
                        )}
                        <h1 className={styles.title}>{props.title}</h1>
                        <hr className={styles.divider} />
                        {props.intro && <RichText document={props.intro} classNameRoot={styles.intro} />}
                    </div>
                </Container>

                {props.mainImage && (
                    <div className={clsx(props.fromModal && styles.animateimage)}>
                        <SBImage image={props.mainImage} height={0} width={0} className={styles.image} />
                    </div>
                )}

                {props.author && (
                    <>
                        <Container code="standard">
                            <div className={styles.author}>
                                <Typography.H4>{microcopy.articleBy}</Typography.H4>
                                <span>&nbsp;</span>
                                <Typography.H4 className={styles.name}>{props.author.name}</Typography.H4>
                            </div>
                            {role && <Typography.Small className={styles.subtitleAuthor}>{role}</Typography.Small>}
                            <hr className={styles.hr} />
                            <div className={styles.date}>
                                {props.component === 'article' && (
                                    <>
                                        <Typography.Small>{readingTime}</Typography.Small>
                                        <Typography.Small className={styles.dot}>•</Typography.Small>
                                    </>
                                )}
                                <Typography.Small>{formattedDate}</Typography.Small>
                            </div>
                        </Container>
                    </>
                )}
                {props.component === 'job' && (
                    <Container code="standard">
                        <div className={styles.date}>
                            <hr className={styles.hr} />
                            <Typography.Small>{formattedDate}</Typography.Small>
                        </div>
                    </Container>
                )}
            </motion.div>
        </Container>
    );
};
export default ArticleHero;
